import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IoService } from '../../../services/io/io.service';
import { AppComponent } from 'src/app/app.component';

// One table row: an existing vendor (from costRows) or a newly added one.
// Costs are edited in dollars and converted to integer cents on save; a
// cleared cost on an existing row deletes that vendor's override so the
// scalar cost applies again.
interface VendorCostRow {
  vendorId: string;
  vendorName: string;
  cost: number | null;
  originalCost: number | null;
  effectiveCost: number | null;
  isNew: boolean;
  removed: boolean;
}

@Component({
  selector: 'app-inventory-vendor-pricing',
  templateUrl: './inventory-vendor-pricing.component.html',
  styleUrls: ['./inventory-vendor-pricing.component.scss']
})
export class InventoryVendorPricingComponent implements OnInit {

  accountName: string = null;
  rows: VendorCostRow[] = [];
  loading = false;
  saving = false;
  loadError: string = null;
  errors: string[] = [];

  // Full vendor list (Vendor.manage-gated server side), filtered client side.
  vendors: any[] = [];
  vendorSearchText = '';
  vendorResults: any[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<InventoryVendorPricingComponent>,
    private ioService: IoService,
  ) { }

  ngOnInit() {
    this.load();
  }

  async load() {
    this.loading = true;
    this.loadError = null;
    try {
      const pricing: any = await this.ioService.post('/account/getInventoryPricing', {
        accountId: this.data.documentId,
      });
      this.applyPricing(pricing);

      const vendors: any = await this.ioService.post('/location/getLocations', {
        type: 'vendor',
      });
      this.vendors = vendors || [];
    } catch (e) {
      this.loadError = e?.error?.msg || 'Failed to load vendor pricing.';
    }
    this.loading = false;
  }

  private applyPricing(pricing: any) {
    this.accountName = pricing.name;
    this.rows = (pricing.costRows || []).map(row => ({
      vendorId: String(row.vendorId),
      vendorName: row.vendorName,
      cost: row.cost !== null && row.cost !== undefined ? row.cost / 100 : null,
      originalCost: row.cost !== null && row.cost !== undefined ? row.cost : null,
      effectiveCost: row.effectiveCost !== null && row.effectiveCost !== undefined ? row.effectiveCost : null,
      isNew: false,
      removed: false,
    }));
  }

  // ----- Vendor search / add -----

  searchVendors() {
    const text = this.vendorSearchText.trim().toLowerCase();
    if (!text) {
      this.vendorResults = [];
      return;
    }
    const usedIds = new Set(this.rows.filter(row => !this.removedForGood(row)).map(row => row.vendorId));
    this.vendorResults = this.vendors
      .filter(vendor => !usedIds.has(String(vendor._id)) && (vendor.name || '').toLowerCase().includes(text))
      .slice(0, 8);
  }

  addVendor(vendor: any) {
    const vendorId = String(vendor._id);
    const existing = this.rows.find(row => row.vendorId === vendorId);
    if (existing) {
      // Re-adding a vendor staged for removal just un-stages it.
      existing.removed = false;
    } else {
      this.rows.push({
        vendorId,
        vendorName: vendor.name,
        cost: null,
        originalCost: null,
        effectiveCost: null,
        isNew: true,
        removed: false,
      });
    }
    this.vendorSearchText = '';
    this.vendorResults = [];
  }

  removeRow(row: VendorCostRow) {
    if (row.isNew) {
      this.rows = this.rows.filter(r => r !== row);
    } else {
      row.removed = !row.removed;
    }
  }

  private removedForGood(row: VendorCostRow): boolean {
    return row.removed && !row.isNew;
  }

  // ----- Save -----

  private toCents(dollars: number): number {
    return Math.round(dollars * 100);
  }

  rowInvalid(row: VendorCostRow): boolean {
    return row.isNew && !row.removed && (row.cost === null || row.cost === undefined || String(row.cost) === '');
  }

  hasChanges(): boolean {
    return this.rows.some(row =>
      row.isNew || row.removed ||
      (row.cost !== null && String(row.cost) !== '' ? this.toCents(row.cost) : null) !== row.originalCost
    );
  }

  async save() {
    this.errors = [];

    const invalidRows = this.rows.filter(row => this.rowInvalid(row));
    if (invalidRows.length > 0) {
      this.errors = ['Cost is required for ' + invalidRows.map(row => row.vendorName).join(', ') + '.'];
      return;
    }

    const costLocation: any = {};
    const addVendorIds: string[] = [];
    const removeVendorIds: string[] = [];

    for (const row of this.rows) {
      if (this.removedForGood(row)) {
        removeVendorIds.push(row.vendorId);
        continue;
      }
      if (row.isNew) {
        addVendorIds.push(row.vendorId);
        costLocation[row.vendorId] = this.toCents(row.cost);
        continue;
      }
      const newValue = (row.cost !== null && row.cost !== undefined && String(row.cost) !== '')
        ? this.toCents(row.cost) : null;
      if (newValue !== row.originalCost) {
        // Empty string deletes the override server side.
        costLocation[row.vendorId] = newValue !== null ? newValue : '';
      }
    }

    this.saving = true;
    try {
      const pricing: any = await this.ioService.post('/account/updateInventoryPricing', {
        accountId: this.data.documentId,
        costLocation,
        addVendorIds,
        removeVendorIds,
      });
      this.applyPricing(pricing);
      AppComponent.showSnack('Vendors and costs saved.', 'OK', 3000);
    } catch (e) {
      this.errors = [e?.error?.msg || 'Failed to save vendor pricing.'];
    }
    this.saving = false;
  }

  close() {
    this.dialogRef.close();
  }
}
